import { createSlice } from "@reduxjs/toolkit";

import type { CommonSettingsData } from "@src/types";
import type * as TDT from "@tradingdroid/frontends-types";

import type { ActionCreatorWithPayload, PayloadAction } from "@reduxjs/toolkit";

export interface ReduxDataState {
  exchanges: TDT.ApiExchange[];
  strategies: TDT.ApiStrategy[];
  backtests: TDT.ApiBacktest[];
  executions: TDT.ApiExecution[];
  optimizations: TDT.ApiStudy[];
  jobs: TDT.ApiJob[];
  backtestFromExternal: (CommonSettingsData & { config: string }) | null;
  symbols: Record<string, TDT.ApiSymbol>;
  exchangeSymbols: Record<string, string[]>; // Per-exchange symbol names cache (session-only)
  isPreloading: boolean;
}

const initialState: ReduxDataState = {
  exchanges: [],
  strategies: [],
  backtests: [],
  executions: [],
  optimizations: [],
  jobs: [],
  backtestFromExternal: null,
  symbols: {},
  exchangeSymbols: {},
  isPreloading: true,
};

// Let TypeScript infer action types (don't annotate as Slice<> - loses payload types)
const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    removeStateBacktest: (state, action: PayloadAction<number>) => {
      console.log(`[Redux] removeStateBacktest: removing backtest #${action.payload}`);
      state.backtests = state.backtests.filter((backtest) => backtest.id !== action.payload);
    },
    upsertStateBacktest: (state, action: PayloadAction<TDT.ApiBacktest>) => {
      const backtest = action.payload;
      const index = state.backtests.findIndex((item) => item.id === backtest.id);
      if (index === -1) {
        // Insert at the beginning to maintain DESC order (newest first)
        state.backtests.unshift(backtest);
      } else {
        state.backtests[index] = backtest;
      }
    },
    upsertStateOptimization: (state, action: PayloadAction<TDT.ApiStudy>) => {
      const study = action.payload;
      const index = state.optimizations.findIndex((item) => item.study_id === study.study_id);
      if (index === -1) {
        // Insert at the beginning to maintain DESC order (newest first)
        state.optimizations.unshift(study);
      } else {
        state.optimizations[index] = study;
      }
    },
    removeStateOptimization: (state, action: PayloadAction<number>) => {
      console.log(`[Redux] removeStateOptimization: removing optimization #${action.payload}`);
      state.optimizations = state.optimizations.filter((opt) => opt.study_id !== action.payload);
    },
    removeStateExecution: (state, action: PayloadAction<number>) => {
      console.log(`[Redux] removeStateExecution: removing execution #${action.payload}`);
      state.executions = state.executions.filter((execution) => execution.id !== action.payload);
    },
    setBacktestFromExternal: (
      state,
      action: PayloadAction<CommonSettingsData & { config: string }>,
    ) => {
      console.log(`[Redux] setBacktestFromExternal: importing backtest configuration`);
      state.backtestFromExternal = action.payload;
    },
    clearBacktestFromExternal: (state) => {
      state.backtestFromExternal = null;
    },
    setStateSymbol: (state, action: PayloadAction<{ key: string; symbol: TDT.ApiSymbol }>) => {
      const { key, symbol } = action.payload;
      state.symbols[key] = symbol;
    },
    setStateSymbols: (state, action: PayloadAction<Record<string, TDT.ApiSymbol>>) => {
      state.symbols = { ...state.symbols, ...action.payload };
    },
    setExchangeSymbolNames: (
      state,
      action: PayloadAction<{ exchange: string; names: string[] }>,
    ) => {
      state.exchangeSymbols[action.payload.exchange] = action.payload.names;
    },

    // ========================================
    // SET ACTIONS - Replace entire state
    // Always maintain DESC order (newest first) for consistent display
    // ========================================
    setBacktests: (state, action: PayloadAction<TDT.ApiBacktest[]>) => {
      state.backtests = [...action.payload].sort((a, b) => b.id - a.id);
    },
    setExecutions: (state, action: PayloadAction<TDT.ApiExecution[]>) => {
      state.executions = [...action.payload].sort((a, b) => b.id - a.id);
    },
    setStrategies: (state, action: PayloadAction<TDT.ApiStrategy[]>) => {
      state.strategies = [...action.payload].sort((a, b) => b.id - a.id);
    },
    setOptimizations: (state, action: PayloadAction<TDT.ApiStudy[]>) => {
      state.optimizations = [...action.payload].sort((a, b) => b.study_id - a.study_id);
    },
    setExchanges: (state, action: PayloadAction<TDT.ApiExchange[]>) => {
      state.exchanges = [...action.payload].sort((a, b) => a.name.localeCompare(b.name));
    },
    setJobs: (state, action: PayloadAction<TDT.ApiJob[]>) => {
      state.jobs = [...action.payload].sort((a, b) => b.id - a.id);
    },

    // ========================================
    // UPSERT ACTIONS - Insert or update single items
    // ========================================
    upsertOptimization: (state, action: PayloadAction<TDT.ApiStudy>) => {
      const study = action.payload;
      const existingIndex = state.optimizations.findIndex((o) => o.study_id === study.study_id);
      if (existingIndex >= 0) {
        // Update existing study (in-place, no re-sort needed)
        console.log(
          `[Redux] upsertOptimization: updating study ${study.study_id} with ${study.trials?.length || 0} trials`,
        );
        state.optimizations[existingIndex] = study;
      } else {
        // Insert new study and re-sort to maintain DESC order
        console.log(`[Redux] upsertOptimization: inserting new study ${study.study_id}`);
        state.optimizations = [...state.optimizations, study].sort(
          (a, b) => b.study_id - a.study_id,
        );
      }
    },
    upsertBacktest: (state, action: PayloadAction<TDT.ApiBacktest>) => {
      const backtest = action.payload;
      const existingIndex = state.backtests.findIndex((b) => b.id === backtest.id);
      if (existingIndex >= 0) {
        // Update existing backtest (in-place, no re-sort needed)
        console.log(
          `[Redux] upsertBacktest: updating backtest ${backtest.id} with ${backtest.positions?.length || 0} positions`,
        );
        state.backtests[existingIndex] = backtest;
      } else {
        // Insert new backtest and re-sort to maintain DESC order
        console.log(`[Redux] upsertBacktest: inserting new backtest ${backtest.id}`);
        state.backtests = [...state.backtests, backtest].sort((a, b) => b.id - a.id);
      }
    },
    upsertExecution: (state, action: PayloadAction<TDT.ApiExecution>) => {
      const execution = action.payload;
      const existingIndex = state.executions.findIndex((e) => e.id === execution.id);
      if (existingIndex >= 0) {
        // Update existing execution (in-place, no re-sort needed)
        console.log(
          `[Redux] upsertExecution: updating execution ${execution.id} with ${execution.positions?.length || 0} positions`,
        );
        state.executions[existingIndex] = execution;
      } else {
        // Insert new execution and re-sort to maintain DESC order
        console.log(`[Redux] upsertExecution: inserting new execution ${execution.id}`);
        state.executions = [...state.executions, execution].sort((a, b) => b.id - a.id);
      }
    },
    // For a job row that may leave fields out, such as a list read with a field projection: the row's fields
    // replace the stored ones and the fields it lacks keep their stored values. A job not in the store is
    // inserted as given. Full rows go through upsertJob, which replaces the stored row.
    mergeJob: (state, action: PayloadAction<TDT.ApiJob>) => {
      const job = action.payload;
      const existingIndex = state.jobs.findIndex((item) => item.id === job.id);
      if (existingIndex >= 0) {
        Object.assign(state.jobs[existingIndex], job);
      } else {
        state.jobs = [...state.jobs, job].sort((a, b) => b.id - a.id);
      }
    },
    upsertJob: (state, action: PayloadAction<TDT.ApiJob>) => {
      const job = action.payload;
      const existingIndex = state.jobs.findIndex((j) => j.id === job.id);
      if (existingIndex >= 0) {
        // Update existing job (in-place, no re-sort needed)
        console.log(`[Redux] upsertJob: updating job ${job.id} (status: ${job.status_name})`);
        state.jobs[existingIndex] = job;
      } else {
        // Insert new job and re-sort to maintain DESC order
        console.log(`[Redux] upsertJob: inserting new job ${job.id}`);
        state.jobs = [...state.jobs, job].sort((a, b) => b.id - a.id);
      }
    },

    /**
     * Update a position within an execution's positions array
     * Used for partial updates (e.g., closing a position via command)
     */
    upsertPositionInExecution: (
      state,
      action: PayloadAction<{
        executionId: number;
        position: Partial<TDT.ApiPosition> & { id: number };
      }>,
    ) => {
      const { executionId, position } = action.payload;
      const executionIndex = state.executions.findIndex((e) => e.id === executionId);
      if (executionIndex < 0) {
        console.warn(`[Redux] upsertPositionInExecution: execution ${executionId} not found`);
        return;
      }
      const execution = state.executions[executionIndex];
      if (!execution.positions) {
        console.warn(
          `[Redux] upsertPositionInExecution: execution ${executionId} has no positions`,
        );
        return;
      }
      const positionIndex = execution.positions.findIndex((p) => p.id === position.id);
      if (positionIndex < 0) {
        console.warn(
          `[Redux] upsertPositionInExecution: position ${position.id} not found in execution ${executionId}`,
        );
        return;
      }
      // Merge the partial position update
      console.log(
        `[Redux] upsertPositionInExecution: updating position ${position.id} in execution ${executionId}`,
      );
      execution.positions[positionIndex] = { ...execution.positions[positionIndex], ...position };
    },
    /**
     * Add a movement to a position within an execution
     * Used when a position is closed via command and we need to add the SELL movement
     */
    addMovementToPosition: (
      state,
      action: PayloadAction<{
        executionId: number;
        positionId: number;
        movement: TDT.ApiPositionMovement;
      }>,
    ) => {
      const { executionId, positionId, movement } = action.payload;
      const executionIndex = state.executions.findIndex((e) => e.id === executionId);
      if (executionIndex < 0) {
        console.warn(`[Redux] addMovementToPosition: execution ${executionId} not found`);
        return;
      }
      const execution = state.executions[executionIndex];
      if (!execution.positions) {
        console.warn(`[Redux] addMovementToPosition: execution ${executionId} has no positions`);
        return;
      }
      const positionIndex = execution.positions.findIndex((p) => p.id === positionId);
      if (positionIndex < 0) {
        console.warn(
          `[Redux] addMovementToPosition: position ${positionId} not found in execution ${executionId}`,
        );
        return;
      }
      const position = execution.positions[positionIndex];
      // Initialize movements array if needed and append the new movement
      if (!position.movements) {
        position.movements = [];
      }
      // Check if movement already exists (by id) to avoid duplicates
      const existingIndex = position.movements.findIndex((m) => m.id === movement.id);
      if (existingIndex >= 0) {
        console.log(
          `[Redux] addMovementToPosition: movement ${movement.id} already exists in position ${positionId}`,
        );
        return;
      }
      console.log(
        `[Redux] addMovementToPosition: adding movement ${movement.id} (${movement.movement_type}) to position ${positionId}`,
      );
      position.movements.push(movement);
    },

    setIsPreloading: (state, action: PayloadAction<boolean>) => {
      state.isPreloading = action.payload;
    },
    /**
     * Reset all data state to initial state
     * Used on logout to clear all user-specific data
     */
    resetDataState: () => {
      return initialState;
    },
  },
});

const dataReducer = dataSlice.reducer;
const dataActions = dataSlice.actions;

// Explicitly typed "set" action creator exports for type-safe usage in entityConfigs.ts
// Type assertions are necessary because Redux Toolkit's indexed access type system
// loses the specific payload types when accessing actions from the slice
export const setStrategies = dataSlice.actions.setStrategies as ActionCreatorWithPayload<
  TDT.ApiStrategy[]
>;
export const setExchanges = dataSlice.actions.setExchanges as ActionCreatorWithPayload<
  TDT.ApiExchange[]
>;
export const setBacktests = dataSlice.actions.setBacktests as ActionCreatorWithPayload<
  TDT.ApiBacktest[]
>;
export const setExecutions = dataSlice.actions.setExecutions as ActionCreatorWithPayload<
  TDT.ApiExecution[]
>;
export const setOptimizations = dataSlice.actions.setOptimizations as ActionCreatorWithPayload<
  TDT.ApiStudy[]
>;
export const setJobs = dataSlice.actions.setJobs as ActionCreatorWithPayload<TDT.ApiJob[]>;

export { dataActions, dataReducer };
